import { Button, TextField, WithStyles, Typography, Box, Checkbox } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { CancelOutlined } from '@material-ui/icons';
import { Autocomplete } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { ICylinderInDTO } from '../../repository/jago/model/input/ICylinderInDTO';
import { IKeyInDTO } from '../../repository/jago/model/input/IKeyInDTO';
import { getDomainNameFromHostname } from '../../repository/utils';
import { IMechanicalCylinderKeyRelationsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import ReduxLanguage from '../ReduxLanguage';
import moment from 'moment';
import DomainIcon from '@material-ui/icons/Domain';
import FileIcon from '@material-ui/icons/FileCopyOutlined';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    padding: 10,
    alignItems: 'center',
  },
  filterItem: {
    marginRight: 10,
    minWidth: 180,
  },
});

export interface IMksRelationsFilterBarProps extends WithStyles<typeof styles> {
  onSearch: (params: IMechanicalCylinderKeyRelationsRequestParamsDTO) => void;
  onResetFilters: () => void;
  onFilterDomains: (value: string) => void;
  onDomainSelect: (domain: IDomainOptionDTO | null) => void;
  domainsOptions: IDomainOptionDTO[];
  selectedHostname: string;
  selectedDomain: IDomainOptionDTO | null;
  isVisible?: boolean;
  lastImport?: IMksImportInDTO | {};
  cylinders: ICylinderInDTO[];
  keys: IKeyInDTO[];
}

export interface IMksRelationsFilterBarState {
  domainSearchText: string;
  selectedCylinders: ICylinderInDTO[];
  selectedKeys: IKeyInDTO[];
  cylinderSearchText: string;
  keySearchText: string;
}


class MksRelationsFilterBar extends React.Component<IMksRelationsFilterBarProps, IMksRelationsFilterBarState> {
  
  constructor(props: IMksRelationsFilterBarProps) {
    super(props);
    this.state = {
      domainSearchText: this.props.selectedDomain ? this.props.selectedDomain.label : '',
      selectedCylinders: [],
      selectedKeys: [],
      cylinderSearchText: '',
      keySearchText: '',
    }
  }

  componentDidUpdate(prevProps: IMksRelationsFilterBarProps) {
    if (prevProps.selectedDomain !== this.props.selectedDomain) {
      const { selectedDomain } = this.props;
      if (selectedDomain) {
        this.setState({ domainSearchText: selectedDomain.label });
      } else {
        this.setState({ domainSearchText: '' });
      }
    }
  }

  public onSearch() {
    const { onSearch, selectedDomain } = this.props;
    const { selectedCylinders, selectedKeys } = this.state;
    
    if (!selectedDomain) return;

    const params: IMechanicalCylinderKeyRelationsRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: 0,
      pageSize: 5000,
      cylinderIds: selectedCylinders && selectedCylinders.length > 0 ? selectedCylinders.map(c => c.id) : undefined,
      keyIds: selectedKeys && selectedKeys.length > 0 ? selectedKeys.map(k => k.id) : undefined,
    };
    
    onSearch(params);
  }

  public onResetFilters() {
    const { onResetFilters } = this.props;
    this.setState({ 
      domainSearchText: '',
      selectedCylinders: [],
      selectedKeys: [],
      cylinderSearchText: '',
      keySearchText: '',
    });
    onResetFilters();
  }

  public onSelectDomain(domain: IDomainOptionDTO | null) {
    const { onDomainSelect, onResetFilters } = this.props;
    
    if (domain) {
      onDomainSelect(domain);
      this.setState({ 
        domainSearchText: domain.label,
        selectedCylinders: [],
        selectedKeys: [],
        cylinderSearchText: '',
        keySearchText: '',
      }, () => {
        this.onSearch();
      });
    } else {
      this.setState({ domainSearchText: '' });
      onResetFilters();
    }
  }

  public onSelectCylinders(cylinders: ICylinderInDTO[]) {
    // Mi assicuro di NON aggiungere doppioni!
    let newArray = cylinders || [];
    newArray = newArray.filter((item: ICylinderInDTO, index: number, self: ICylinderInDTO[]) =>
      index === self.findIndex((t) => t.id === item.id)
    );
    this.setState({ 
      selectedCylinders: newArray,
    }, () => {
      if (this.props.selectedDomain) {
        this.onSearch();
      }
    });
  }

  public onSelectKeys(keys: IKeyInDTO[]) {
    // Mi assicuro di NON aggiungere doppioni!
    let newArray = keys || [];
    newArray = newArray.filter((item: IKeyInDTO, index: number, self: IKeyInDTO[]) =>
      index === self.findIndex((t) => t.id === item.id)
    );
    this.setState({ 
      selectedKeys: newArray,
    }, () => {
      if (this.props.selectedDomain) {
        this.onSearch();
      }
    });
  }

  onFetchDomains(value: string) {
    const { onFilterDomains } = this.props;
    onFilterDomains(value);
  }

  public render() {
    const { classes, domainsOptions, selectedDomain, isVisible = true, lastImport, cylinders, keys } = this.props;
    const { domainSearchText, selectedCylinders, selectedKeys, cylinderSearchText, keySearchText } = this.state;
    const debouncedDomainSearch = _.debounce((text) => this.onFetchDomains(text), 500);

    return (
      <div className={classes.container} style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, width: '100%' }}>
          <div className={classes.filterItem}>
            <Autocomplete
              options={domainsOptions}
              inputValue={domainSearchText}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (option.label) return option.label;
                if (option.value?.name) return option.value.name;
                if (option.value?.hostname) return getDomainNameFromHostname(option.value.hostname);
                return '';
              }}
              isOptionEqualToValue={(option, value) => option?.value?.hostname === value?.value?.hostname}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  variant="outlined" 
                  label={<ReduxLanguage languageKey="mks.hostnameRequired" />}
                  required
                  error={!selectedDomain}
                  helperText={''}
                  style={{ minWidth: 250 }}
                />
              )}
              onInputChange={(event, newInputValue) => { 
                this.setState({ domainSearchText: newInputValue }); 
                debouncedDomainSearch(newInputValue); 
              }}
              onChange={(event, newValue) => this.onSelectDomain(newValue)}
              noOptionsText="No domains found"
              loadingText="Loading domains..."
              clearOnBlur={false}
              clearOnEscape={true}
            />
          </div>
          {isVisible && selectedDomain && (
            <>
           <div className={classes.filterItem} style={{ maxWidth: 350 }}>
             <Autocomplete
               id="cylinders-selection"
               disableCloseOnSelect
               options={cylinders}
               multiple
               value={selectedCylinders}
               inputValue={cylinderSearchText}
               limitTags={1}
               getOptionLabel={(option) => {
                 if (!option) return '';
                 return option.doorName || `Cylinder ${option.id}`;
               }}
               isOptionEqualToValue={(option, value) => option?.id === value?.id}
               onChange={(event, newValue) => {
                 this.onSelectCylinders(newValue || []);
               }}
               renderOption={(props, option: ICylinderInDTO) => {
                 const isSelected = selectedCylinders.some((c: ICylinderInDTO) => c.id === option.id);
                 return (
                   <li {...props} key={option.id}>
                     <div style={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       width: '100%',
                       backgroundColor: isSelected ? 'rgba(100, 149, 237, 0.15)' : 'transparent',
                       padding: '8px 4px',
                       borderRadius: '4px'
                     }}>
                       <Checkbox color="primary" checked={isSelected} style={{ padding: '4px 8px' }} />
                       <span style={{ 
                         flex: 1, 
                         fontWeight: isSelected ? 'bold' : 'normal',
                         color: isSelected ? 'cornflowerblue' : 'inherit'
                       }}>
                         {option.doorName || `Cylinder ${option.id}`}
                       </span>
                     </div>
                   </li>
                 );
               }}
               renderInput={(params) => (
                 <TextField 
                   {...params} 
                   variant="outlined" 
                   label={<ReduxLanguage languageKey="mks.cylinders" />}
                   placeholder=""
                   style={{ minWidth: 200, maxWidth: 350 }}
                 />
               )}
               onInputChange={(event, newInputValue) => {
                 this.setState({ cylinderSearchText: newInputValue });
               }}
               noOptionsText="No cylinders found"
               loadingText="Loading cylinders..."
               clearOnBlur={false}
               clearOnEscape={true}
               sx={{
                 '& .MuiAutocomplete-tag': {
                   maxWidth: 'calc(100% - 40px)',
                   overflow: 'hidden',
                   textOverflow: 'ellipsis',
                 },
                 '& .MuiAutocomplete-inputRoot': {
                   flexWrap: 'nowrap',
                 },
               }}
             />
           </div>

           <div className={classes.filterItem} style={{ maxWidth: 350 }}>
             <Autocomplete
               id="keys-selection"
               disableCloseOnSelect
               options={keys}
               multiple
               value={selectedKeys}
               inputValue={keySearchText}
               limitTags={1}
               getOptionLabel={(option) => {
                 if (!option) return '';
                 return option.keyFunction || `Key ${option.id}`;
               }}
               isOptionEqualToValue={(option, value) => option?.id === value?.id}
               onChange={(event, newValue) => {
                 this.onSelectKeys(newValue || []);
               }}
               renderOption={(props, option: IKeyInDTO) => {
                 const isSelected = selectedKeys.some((k: IKeyInDTO) => k.id === option.id);
                 return (
                   <li {...props} key={option.id}>
                     <div style={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       width: '100%',
                       backgroundColor: isSelected ? 'rgba(100, 149, 237, 0.15)' : 'transparent',
                       padding: '8px 4px',
                       borderRadius: '4px'
                     }}>
                       <Checkbox color="primary" checked={isSelected} style={{ padding: '4px 8px' }} />
                       <span style={{ 
                         flex: 1, 
                         fontWeight: isSelected ? 'bold' : 'normal',
                         color: isSelected ? 'cornflowerblue' : 'inherit'
                       }}>
                         {option.keyFunction || `Key ${option.id}`}
                       </span>
                     </div>
                   </li>
                 );
               }}
               renderInput={(params) => (
                 <TextField 
                   {...params} 
                   variant="outlined" 
                   label={<ReduxLanguage languageKey="mks.keys" />}
                   placeholder=""
                   style={{ minWidth: 180, maxWidth: 350 }}
                 />
               )}
               onInputChange={(event, newInputValue) => {
                 this.setState({ keySearchText: newInputValue });
               }}
               noOptionsText="No keys found"
               loadingText="Loading keys..."
               clearOnBlur={false}
               clearOnEscape={true}
               sx={{
                 '& .MuiAutocomplete-tag': {
                   maxWidth: 'calc(100% - 40px)',
                   overflow: 'hidden',
                   textOverflow: 'ellipsis',
                 },
                 '& .MuiAutocomplete-inputRoot': {
                   flexWrap: 'nowrap',
                 },
               }}
             />
           </div>
           </>
           )}

          <Button
            variant="contained"
            style={{ 
              backgroundColor: 'red', 
              height: 40, 
              color: 'white', 
              fontWeight: 'bold',
              minWidth: 'auto',
              padding: '8px 16px'
            }}
            onClick={() => this.onResetFilters()}
          >
            <CancelOutlined style={{ color: 'white' }} />
          </Button>
          {lastImport && Object.keys(lastImport).length > 0 && (
            <Box style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingRight: '10px' }}>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#666', fontSize: 17 }}>
                <ReduxLanguage languageKey="mks.currentConfig" />
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <DomainIcon style={{ marginRight: 5, color: '#333', fontSize: 16 }} />
                <Typography variant="body2" style={{ color: '#333' }}>
                  {(getDomainNameFromHostname(selectedDomain?.value?.hostname))}
                </Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FileIcon style={{ marginRight: 5, color: '#333', fontSize: 16 }} />
              <Typography variant="body2" style={{ color: '#333' }}>
                {(lastImport as IMksImportInDTO).filename}
              </Typography>
              </div>
              <Typography variant="caption" style={{ color: '#999' }}>
                {moment((lastImport as IMksImportInDTO).importTimestamp).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Box>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(MksRelationsFilterBar);

